import { Fragment } from "react";
import { cn } from "@/lib/utils";
import {
  parseMarkdown,
  safeHref,
  spansToText,
  type Align,
  type Block,
  type Span,
} from "@gb/contracts";

/**
 * Markdown, rendered.
 *
 * Every node below is a React element. Nothing on this path takes a string of
 * HTML, which is what makes it safe to point at a parsed customer document —
 * see `markdown.ts` for the parser's side of that.
 *
 * **Trust is a prop, because the two callers are not equally trusted.**
 *
 * - `model` — a chat answer. Prose we asked for, in a shape we prompted. Links
 *   become anchors, on an allow-listed scheme.
 * - `document` — text extracted from a file somebody uploaded. A link here was
 *   written by whoever wrote the PDF, so it renders as *what it says plus where
 *   it goes*, and is not clickable. A convincing "click here to re-authenticate"
 *   in a spec sheet is a phishing link with our chrome around it, and there is
 *   no reading of the Brain that needs the anchor to work.
 *
 * Both run the scheme allow-list. A model happily quotes a URL it read.
 */
export function MarkdownView({
  text,
  trust,
  renderMarker,
  className,
}: {
  text: string;
  trust: "model" | "document";
  /**
   * Renders a `[[name:arg]]` marker. Returning `null` drops it — which is what
   * a chat citation with no matching source does, an unclickable `[4]` beside a
   * rail that stops at 3 being a bug the reader can do nothing about.
   *
   * With no handler at all a marker stays literal text, so a document that
   * happens to contain double brackets is quoted, not silently edited.
   */
  renderMarker?: (marker: { name: string; arg: string }) => React.ReactNode;
  className?: string;
}) {
  return (
    // Type size, leading and colour are the caller's — the same tree renders at
    // answer scale in chat and at segment scale in the Brain. Everything below
    // sizes in `em` so both stay proportionate.
    <div className={cn("flex flex-col gap-3 leading-[1.6]", className)}>
      <Blocks blocks={parseMarkdown(text)} trust={trust} renderMarker={renderMarker} />
    </div>
  );
}

type Ctx = {
  trust: "model" | "document";
  renderMarker?: (marker: { name: string; arg: string }) => React.ReactNode;
};

const HEADINGS: Record<number, string> = {
  1: "font-display text-[1.15em] leading-[1.2] font-bold tracking-[0.01em] text-ink uppercase",
  2: "font-display text-[1.02em] leading-[1.25] font-bold tracking-[0.02em] text-ink uppercase",
};
/** `###` and below are the small label the chat answers have always used. */
const SUB_HEADING =
  "font-display text-[0.78em] leading-[1.3] font-semibold tracking-[0.1em] text-muted uppercase";

const ALIGN: Record<Align, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

function Blocks({ blocks, ...ctx }: Ctx & { blocks: readonly Block[] }) {
  return (
    <>
      {blocks.map((block, index) => (
        <Fragment key={index}>{renderBlock(block, ctx)}</Fragment>
      ))}
    </>
  );
}

function renderBlock(block: Block, ctx: Ctx): React.ReactNode {
  switch (block.type) {
    case "heading": {
      const Tag = `h${Math.min(block.level + 1, 6)}` as "h2";
      return (
        <Tag className={cn("mt-1", HEADINGS[block.level] ?? SUB_HEADING)}>
          <Spans spans={block.spans} {...ctx} />
        </Tag>
      );
    }

    case "para":
      // `pre-wrap` rather than `pre-line`: a run of spaces is alignment in a
      // document that had columns, and the parser has already turned every
      // newline into an explicit break.
      return (
        <p className="whitespace-pre-wrap text-ink-prose">
          <Spans spans={block.spans} {...ctx} />
        </p>
      );

    case "rule":
      return <hr className="my-1 border-0 border-t border-border-soft" />;

    case "quote":
      return (
        <blockquote className="flex flex-col gap-2 border-l-2 border-brand-border pl-3 text-muted italic">
          <Blocks blocks={block.blocks} {...ctx} />
        </blockquote>
      );

    case "code":
      return (
        <pre
          className={cn(
            "overflow-x-auto rounded-control border border-border-soft bg-surface-sunken",
            "px-3 py-2.5 font-mono text-[0.84em] leading-[1.5] text-ink-prose",
          )}
        >
          <code>{block.text}</code>
        </pre>
      );

    case "list": {
      const Tag = block.ordered ? "ol" : "ul";
      return (
        <Tag className="flex list-none flex-col gap-1.5">
          {block.items.map((item, index) => (
            <li key={index} className="relative pl-5 text-ink-prose">
              {item.checked === null ? (
                <span
                  className="absolute left-0 font-display text-[0.84em] font-semibold text-brand"
                  aria-hidden
                >
                  {item.marker}
                </span>
              ) : (
                // A checkbox that cannot be ticked. The list is a rendering of
                // text somebody else wrote; there is nothing here to persist a
                // change to, so it says so by being disabled rather than by
                // silently discarding the click.
                <input
                  type="checkbox"
                  checked={item.checked}
                  disabled
                  aria-label={spansToText(firstLine(item.blocks))}
                  className="absolute left-0 top-[0.35em] size-[0.85em] accent-brand"
                />
              )}
              <div className="flex flex-col gap-1.5">
                <Blocks blocks={item.blocks} {...ctx} />
              </div>
            </li>
          ))}
        </Tag>
      );
    }

    case "table":
      return (
        // The table scrolls inside its own box. A wide spreadsheet must not be
        // able to widen the column it sits in — `architecture/0001` has one
        // scroll region per screen and this is not it.
        <div className="overflow-x-auto rounded-control border border-border-soft">
          <table className="w-full border-collapse text-[0.92em]">
            <thead>
              <tr className="bg-surface-sunken">
                {block.head.map((cell, index) => (
                  <th
                    key={index}
                    scope="col"
                    className={cn(
                      "border-b border-border-soft px-2.5 py-1.5 text-left align-top",
                      "font-display text-[0.9em] font-semibold tracking-[0.04em] text-muted uppercase",
                      ALIGN[block.align[index] ?? "left"],
                    )}
                  >
                    <Spans spans={cell} {...ctx} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-border-faint last:border-0">
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className={cn(
                        "px-2.5 py-1.5 align-top leading-[1.5] text-ink-prose",
                        ALIGN[block.align[cellIndex] ?? "left"],
                      )}
                    >
                      <Spans spans={cell} {...ctx} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

/** The spans of an item's first paragraph — the label a checkbox needs. */
function firstLine(blocks: readonly Block[]): Span[] {
  const first = blocks[0];
  return first?.type === "para" ? first.spans : [];
}

function Spans({ spans, ...ctx }: Ctx & { spans: readonly Span[] }) {
  return (
    <>
      {spans.map((span, index) => (
        <Fragment key={index}>{renderSpan(span, ctx)}</Fragment>
      ))}
    </>
  );
}

function renderSpan(span: Span, ctx: Ctx): React.ReactNode {
  switch (span.type) {
    case "text":
      return span.text;

    case "break":
      return <br />;

    case "strong":
      return (
        <strong className="font-semibold text-ink">
          <Spans spans={span.spans} {...ctx} />
        </strong>
      );

    case "em":
      return (
        <em className="italic">
          <Spans spans={span.spans} {...ctx} />
        </em>
      );

    case "del":
      return (
        <del className="text-muted line-through">
          <Spans spans={span.spans} {...ctx} />
        </del>
      );

    case "code":
      return (
        <code className="rounded-tag bg-subtle px-[0.32em] py-[0.1em] font-mono text-[0.88em] text-ink">
          {span.text}
        </code>
      );

    case "marker": {
      if (!ctx.renderMarker) return `[[${span.name}:${span.arg}]]`;
      return ctx.renderMarker({ name: span.name, arg: span.arg });
    }

    case "link": {
      const href = safeHref(span.href);
      const label = <Spans spans={span.spans} {...ctx} />;

      if (!href || ctx.trust === "document") {
        // Not an anchor. The reader still gets both halves — what it claimed to
        // be and where it actually pointed — which is strictly more than the
        // pre-wrap this replaced ever showed them.
        const shown = spansToText(span.spans).trim();
        return (
          <span className="text-ink-prose">
            {label}
            {span.href && span.href !== shown && (
              <span className="text-faint"> ({span.href})</span>
            )}
          </span>
        );
      }

      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-brand underline decoration-brand/40 underline-offset-2 hover:decoration-brand"
        >
          {label}
        </a>
      );
    }
  }
}
